import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type MemberRole = 'admin' | 'manager' | 'member';

export interface Member {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'employee';
  avatarUrl: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  isYou: boolean;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  createdAt: string;
}

export function useMembers() {
  return useQuery({
    queryKey: ['members'],
    queryFn: async () => {
      const r = await api.get<{ members: Member[]; invites: PendingInvite[] }>('/api/members');
      return r.data;
    },
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { email: string; role: MemberRole }) => {
      const r = await api.post<PendingInvite>('/api/members/invite', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/members/invites/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { userId: string; role: MemberRole }) => {
      await api.patch(`/api/members/${args.userId}/role`, { role: args.role });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => { await api.delete(`/api/members/${userId}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

/** Public — used by the accept-invite page (no session yet). */
export async function acceptInvite(args: { token: string; password: string; displayName?: string }) {
  const r = await api.post<{ user: unknown }>('/api/members/accept', args);
  return r.data;
}
